import { Dispatch, SetStateAction, useCallback, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { ArrowheadRightDarkIcon } from "../../../../../assets";
import { ProjectActivitiesConstants, ProjectPermissionConstants } from "../../../../../constants";
import { CursorChangeProps, Origin, ResultType } from "../../../../../models";
import { IStateOrigin } from "../../../../../navigators/navigateStateObjects";
import {
  getCurrentUserProjectPermissions,
  GetCurrentUserProjectPermissionsResponse,
  searchActivities,
  SearchActivitiesRequest,
  SearchActivitiesResponse,
  SearchActivityHistoryResponse,
} from "../../../../../service/query";
import { FilterGroups, ResultData } from "../../../../../service/Shared";
import { useAuth } from "../../../../../useAuth";
import { flattenObject, hasPermissionForProject } from "../../../../../utils";
import {
  getActivitiesRoute,
  getActivityDashboardTabRoute,
  getActivityViewRoute,
  getProjectDetailsByUuid,
  getProjectManageProjectGroupRoute,
} from "../../../../../utils/routes";
import {
  DataGridButtonLinkCellFormatterData,
  DataGridColumnDefinition,
  DataGridLinkCellFormatterData,
  dataGridMapFilterCriteria,
  Toast,
} from "../../../../../widget";
import { IconCellFormatterData } from "../../../../../widget/data/DataGrid/models";
import { resumeActivity } from "../../../../shared/utils";

interface UseDashboardActivitiesTabReturnData {
  columns: DataGridColumnDefinition[];
  isLoading: boolean;
  setShowVersionConflictModal: Dispatch<SetStateAction<boolean>>;
  showVersionConflictModal: boolean;
  newVersionActivityHistory?: SearchActivityHistoryResponse;
  draftActivityHistoryUuid?: string;
  onChange: DataGridCursorDataLoadEventHandler;
  activeTab: string;
  onTabChange: (value: string) => void;
  onViewMore: () => void;
  toggleTabsList: { label: string }[];
}
const DASHBOARD_ACTIVITIES_IN_PROGRESS = "In progress";
const DASHBOARD_ACTIVITIES_IN_REVIEW = "In review";
type DataGridCursorDataLoadEventHandler = ({ filtering, paging, sorting }: CursorChangeProps) => Promise<ResultType>;

const inProgressColumns: DataGridColumnDefinition[] = [
  {
    key: "projectLink",
    name: "Project / Registry group",
    dataType: "string",
    formatter: "link",
    minWidth: 200,
    sortable: false,
  },
  {
    key: "standardDisplayName",
    name: "Code",
    dataType: "string",
    sortable: false,
  },
  {
    key: "activityLink",
    name: "Activity",
    dataType: "string",
    formatter: "buttonLink",
    sortable: false,
  },
  {
    name: "Status",
    key: "status",
    dataType: "string",
    formatter: "projectActivitiesStatusPill",
    minWidth: 200,
    sortable: false,
  },
  {
    name: "Progress",
    key: "latestVersion.completionPercentage",
    dataType: "number",
    formatter: "progress",
    minWidth: 160,
    sortable: false,
  },
  {
    name: "Last updated",
    key: "latestVersion.updatedAt",
    dataType: "Date",
    formatter: "dateOnly",
    minWidth: 120,
    filterable: false,
    sortable: false,
  },
  {
    key: "activityDetailLink",
    name: "More details",
    dataType: "string",
    formatter: "icon",
    alignment: "center",
    filterable: false,
    sortable: false,
  },
];

const inReviewColumns: DataGridColumnDefinition[] = [
  {
    key: "projectLink",
    name: "Project / Registry group",
    dataType: "string",
    formatter: "link",
    sortable: false,
  },
  {
    key: "standardDisplayName",
    name: "Code",
    dataType: "string",
    sortable: false,
  },
  {
    key: "activityLink",
    name: "Activity",
    dataType: "string",
    formatter: "buttonLink",
    sortable: false,
  },
  {
    name: "Status",
    key: "status",
    dataType: "string",
    formatter: "projectActivitiesStatusPill",
    minWidth: 240,
    sortable: false,
  },
  {
    name: "Verifier",
    key: "verifierDisplayName",
    dataType: "string",
    sortable: false,
  },
  {
    name: "Submission date",
    key: "firstSubmissionDate",
    dataType: "Date",
    formatter: "dateOnly",
    minWidth: 140,
    filterable: false,
    sortable: false,
  },
  {
    name: "Last updated",
    key: "reviewVersion.createdAt",
    dataType: "Date",
    formatter: "dateOnly",
    minWidth: 120,
    filterable: false,
    sortable: false,
  },
  {
    key: "activityDetailLink",
    name: "More details",
    dataType: "string",
    formatter: "icon",
    alignment: "center",
    minWidth: 120,
    filterable: false,
    sortable: false,
  },
];

export const useDashboardActivitiesTab = (): UseDashboardActivitiesTabReturnData => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUserType } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [newVersionActivityHistory, setNewVersionActivityHistory] = useState<SearchActivityHistoryResponse>();
  const [showVersionConflictModal, setShowVersionConflictModal] = useState(false);
  const [draftActivityHistoryUuid, setDraftActivityHistoryUuid] = useState<string>();
  const [activeTab, setActiveTab] = useState(DASHBOARD_ACTIVITIES_IN_PROGRESS);
  const [loadedTab, setLoadedTab] = useState(DASHBOARD_ACTIVITIES_IN_PROGRESS);
  const loadRequestIdRef = useRef(0);

  const pageSize = 10;

  const toggleTabsList = [{ label: DASHBOARD_ACTIVITIES_IN_PROGRESS }, { label: DASHBOARD_ACTIVITIES_IN_REVIEW }];

  const onViewMore = (): void => {
    const suffix = activeTab === DASHBOARD_ACTIVITIES_IN_REVIEW ? "/in_review" : "/in_progress";
    navigate(getActivitiesRoute(currentUserType).concat(suffix), {
      state: { origin: { goBackText: "Back to dashboard", from: location.pathname } as IStateOrigin },
    });
  };

  const onTabChange = (value: string): void => {
    if (value === activeTab) return;

    setActiveTab(value);
    setIsLoading(true);
  };

  const columns = loadedTab === DASHBOARD_ACTIVITIES_IN_REVIEW ? inReviewColumns : inProgressColumns;

  const formatData = useCallback(
    (
      responseData: SearchActivitiesResponse | undefined,
      permissions: GetCurrentUserProjectPermissionsResponse[]
    ): ResultData[] =>
      responseData?.results?.map((el) => {
        const result = flattenObject(el);

        result.projectLink = {
          text: el.project ? el.project.displayName : el.group?.displayName,
          to: el.project
            ? getProjectDetailsByUuid(el.project.uuid, currentUserType)
            : // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              getProjectManageProjectGroupRoute(el.group!.uuid!, `origin=${Origin.DeveloperDashboardPage}`),
          state: { origin: { goBackText: "Back to dashboard", from: location.pathname } as IStateOrigin },
        } as DataGridLinkCellFormatterData;

        result.standardDisplayName = el.project ? el.project.standard.displayName : el.group?.standard?.displayName;
        result.verifierDisplayName = el.project ? el.project.verifier?.displayName : el.group?.verifier?.displayName;

        if (
          (el.status === ProjectActivitiesConstants.STATUS_IN_PROGRESS ||
            el.status === ProjectActivitiesConstants.STATUS_CHANGES_REQUIRED ||
            el.status === ProjectActivitiesConstants.STATUS_REVIEW_CANCELLED) &&
          hasPermissionForProject(
            permissions,
            el.project?.uuid,
            ProjectPermissionConstants.MANAGE_PROJECT_ACTIVITY,
            el.group?.uuid
          )
        ) {
          result.activityLink = {
            text: el.activityDefinition.displayName,
            action: () => {
              if (el.isUnderReview && el.draftVersion) {
                navigate(
                  getActivityViewRoute(
                    el.draftVersion?.uuid,
                    currentUserType,
                    el.project ? `projectUuid=${el.project.uuid}` : ""
                  ),
                  { state: { origin: { goBackText: "Back to dashboard", from: location.pathname } as IStateOrigin } }
                );
              } else if (el.isUnderReview && el.currentVersion) {
                navigate(
                  getActivityViewRoute(
                    el.currentVersion?.uuid,
                    currentUserType,
                    el.project ? `projectUuid=${el.project.uuid}` : ""
                  ),
                  { state: { origin: { goBackText: "Back to dashboard", from: location.pathname } as IStateOrigin } }
                );
              } else {
                resumeActivity(
                  navigate,
                  currentUserType,
                  setDraftActivityHistoryUuid,
                  setNewVersionActivityHistory,
                  setShowVersionConflictModal,
                  el.uuid,
                  el.draftVersion?.uuid,
                  el.draftVersion?.versionNumber
                );
              }
            },
          } as DataGridButtonLinkCellFormatterData;
        } else {
          result.activityLink = {
            text: el.activityDefinition.displayName,
            action: () => {
              if (el.currentVersion) {
                navigate(
                  getActivityViewRoute(
                    el.currentVersion.uuid,
                    currentUserType,
                    el.project ? `projectUuid=${el.project.uuid}` : ""
                  ),
                  { state: { origin: { goBackText: "Back to dashboard", from: location.pathname } as IStateOrigin } }
                );
              } else if (el.reviewVersion) {
                navigate(
                  getActivityViewRoute(
                    el.reviewVersion.uuid,
                    currentUserType,
                    el.project ? `projectUuid=${el.project.uuid}` : ""
                  ),
                  { state: { origin: { goBackText: "Back to dashboard", from: location.pathname } as IStateOrigin } }
                );
              } else {
                Toast.error({
                  message: `This activity has not yet been published. Once ${el.createdByUser.fullName} has published the activity, you can view the activity.`,
                });
              }
            },
          } as DataGridButtonLinkCellFormatterData;
        }

        result.activityDetailLink = {
          action: () =>
            navigate(
              getActivityDashboardTabRoute(
                el.uuid,
                "documents",
                currentUserType,
                el.project ? `projectUuid=${el.project.uuid}` : ""
              ),
              { state: { origin: { goBackText: "Back to dashboard", from: location.pathname } as IStateOrigin } }
            ),
          icon: <ArrowheadRightDarkIcon width={12} height={12} />,
        } as IconCellFormatterData;

        return result;
      }) || [],
    []
  );

  const onChange = async ({ filtering, paging }: CursorChangeProps): Promise<ResultType> => {
    const filterCriteria = dataGridMapFilterCriteria(filtering);

    const projectOrGroupNameFilter = filterCriteria.projectLink;
    filterCriteria.projectLink = undefined;

    if (filterCriteria.activityLink) {
      filterCriteria.activityDefinition = {
        displayName: filterCriteria.activityLink,
      };
      filterCriteria.activityLink = undefined;
    }

    if (filterCriteria.standardDisplayName) {
      filterCriteria.project = {
        ...filterCriteria.project,
        standard: { displayName: filterCriteria.standardDisplayName },
      };
      filterCriteria.standardDisplayName = undefined;
    }

    if (filterCriteria.verifierDisplayName) {
      filterCriteria.project = {
        ...filterCriteria.project,
        verifier: { displayName: filterCriteria.verifierDisplayName },
      };
      filterCriteria.verifierDisplayName = undefined;
    }

    const requestedTab = activeTab;
    const isInReview = requestedTab === DASHBOARD_ACTIVITIES_IN_REVIEW;

    loadRequestIdRef.current += 1;
    const requestId = loadRequestIdRef.current;

    filterCriteria.status = {
      operator: "in",
      value: isInReview ? ProjectActivitiesConstants.INREVIEW_STATUSES : ProjectActivitiesConstants.INPROGESS_STATUSES,
    };
    filterCriteria.isUnderReview = {
      operator: "eq",
      value: isInReview,
    };

    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    setIsLoading(true);

    const request: SearchActivitiesRequest & FilterGroups<SearchActivitiesRequest["filter"]> = {
      paging: {
        beforeCursor: paging.beforeCursor || null,
        afterCursor: paging.afterCursor || null,
        limit: pageSize,
      },
      sort: [
        {
          key: "latestVersion.updatedAt" as "results.latestVersion.updatedAt",
          direction: "desc",
        },
      ],
      filter: { results: filterCriteria },
    };

    if (projectOrGroupNameFilter) {
      request.filterGroups = [
        {
          operator: "or",
          filter: {
            results: {
              project: { displayName: projectOrGroupNameFilter },
              group: { displayName: projectOrGroupNameFilter },
            },
          },
        },
      ];
    }

    await searchActivities(request)
      .then(async (response) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const projectUuids = response.data?.results?.filter((x) => x.project).map((el) => el.project!.uuid) ?? null;
        const groupUuids =
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          response.data?.results?.filter((x) => x.group?.uuid != null).map((el) => el.group!.uuid!) ?? null;
        await getCurrentUserProjectPermissions({ projectUuids, groupUuids }).then((permissions) => {
          const resultData = formatData(response.data, permissions.data || []);
          data = {
            resultData,
            paging: {
              startCursor: response.data?.paging?.startCursor || "",
              endCursor: response.data?.paging?.endCursor || "",
              pageSize,
              // Report the displayed row count, not the full total.
              totalCount: resultData.length,
              hasNextPage: false,
              hasPreviousPage: false,
            },
          };
          if (requestId === loadRequestIdRef.current) setLoadedTab(requestedTab);
        });
      })
      .finally(() => {
        if (requestId === loadRequestIdRef.current) setIsLoading(false);
      });

    return data;
  };

  return {
    columns,
    isLoading,
    setShowVersionConflictModal,
    showVersionConflictModal,
    newVersionActivityHistory,
    draftActivityHistoryUuid,
    onChange,
    activeTab,
    onTabChange,
    onViewMore,
    toggleTabsList,
  };
};
